import { useContentDeliveryAPI, useEpiserver } from '@episerver/spa-core';
import IContentDeliveryApi from '@episerver/spa-core/dist/ContentDelivery/IContentDeliveryAPI';
import { HelpFilledIcon, Icon, Popover, PopoverGroup, Typography } from '@norges-domstoler/dds-components';
import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { useLocation } from 'react-router';
import WordsPageData from '../../../Models/Content/WordsPageData';

const POPOVER_CLASS = 'popover';
const GUID_RE = /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/;

type Target = {
    key: number;
    host: HTMLElement;
    guid: string;
    label: string;
};

const wordCache = new Map<string, Promise<WordsPageData>>();

function fetchWord(api: IContentDeliveryApi, guid: string): Promise<WordsPageData> {
    let promise = wordCache.get(guid);
    if (!promise) {
        promise = api.getContent<WordsPageData>(guid).then((w) => w as WordsPageData);
        wordCache.set(guid, promise);
        promise.catch(() => wordCache.delete(guid));
    }
    return promise;
}

async function loadEntry(api: IContentDeliveryApi, guid: string): Promise<readonly [string, WordsPageData] | null> {
    try {
        const word = await fetchWord(api, guid);
        return [guid, word] as const;
    } catch (err) {
        console.log('Error loading popover word', guid, err);
        return null;
    }
}

/**
 * Replaces every <span class="popover" id="<guid>">...</span> in the rendered
 * XHTML content with a DDS Popover. All targets are captured first; each unique
 * word is then fetched once (with caching) so the popover content is ready
 * before the user opens it.
 */
export const OrdlistePopovers = () => {
    const ctx = useEpiserver();
    const api = useContentDeliveryAPI();
    const location = useLocation();
    const [targets, setTargets] = useState<Target[]>([]);
    const [words, setWords] = useState<Map<string, WordsPageData>>(new Map());
    const isSSR = ctx.isServerSideRendering();

    useEffect(() => {
        if (isSSR) return;

        let nextKey = 0;
        setTargets([]);
        setWords(new Map());

        const captureTargets = () => {
            const spans = Array.from(document.getElementsByClassName(POPOVER_CLASS));
            const captured: Target[] = [];

            for (const el of spans) {
                if (!(el instanceof HTMLElement) || !GUID_RE.test(el.id)) continue;

                captured.push({ key: nextKey++, host: el, guid: el.id, label: el.innerText });
                el.removeAttribute('id');
                el.classList.remove(POPOVER_CLASS);
                el.innerHTML = '';
            }

            if (captured.length === 0) return;

            setTargets((prev) => [...prev, ...captured]);

            const uniqueGuids = [...new Set(captured.map((t) => t.guid))];

            Promise.all(uniqueGuids.map((guid) => loadEntry(api, guid))).then((results) => {
                setWords((prev) => {
                    const next = new Map(prev);
                    for (const entry of results) {
                        if (entry) next.set(entry[0], entry[1]);
                    }
                    return next;
                });
            });
        };

        captureTargets();

        const observer = new MutationObserver(captureTargets);
        observer.observe(document.body, { childList: true, subtree: true });

        return () => observer.disconnect();
    }, [api, isSSR, location.pathname]);

    if (isSSR) return null;

    return (
        <>
            {targets.map((t) => (
                <PopoverPortal key={t.key} target={t} word={words.get(t.guid) ?? null} />
            ))}
        </>
    );
};

const PopoverPortal = ({ target, word }: { target: Target; word: WordsPageData | null }) => {
    const [isOpen, setIsOpen] = useState(false);

    const header = word?.name?.toString() ?? target.label;
    const description = word?.wordDescription?.value ?? ''; // Viser tom popover hvis beskrivelsen mangler eller feiler å laste

    return createPortal(
        <PopoverGroup popoverId={`popover-${target.guid}-${target.key}`} isOpen={isOpen} setIsOpen={setIsOpen}>
            <button className="pop-button focus-internal-link" type="button">
                {target.label}
                <Icon iconSize="small" icon={HelpFilledIcon} />
            </button>
            <Popover
                header={header}
                placement="bottom-end"
                withCloseButton
                returnFocusOnBlur={true}
                onBlur={() => setIsOpen(false)}
                // xhtml-container-link-styling fungerer bare inne i en mosey-layout.
                // Vi bruker portal og havner utenfor main containeren som har mosey-layout.
                className="mosey-layout"
                // portal gjør at popover-innholdet ikke havner inne i span-elementet.
                // Det gjør også at HeaderBox ikke trenger å filtere vekk popover-innhold fra h2-teksten.
                portal
            >
                {word && (
                    <Typography
                        style={{ maxWidth: '500px' }}
                        className="popover-typography xhtml-container-link-styling"
                        typographyType="bodyLongMedium"
                        as="div"
                    >
                        <div dangerouslySetInnerHTML={{ __html: description }}></div>
                    </Typography>
                )}
            </Popover>
        </PopoverGroup>,
        target.host
    );
};

export default OrdlistePopovers;
